import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlusCircle, FolderKanban, FileBarChart, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner, EmptyState, Badge } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { formatDate } from '@/lib/utils';

export function AppHome() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
  });

  const projects = data?.projects ?? [];
  const firstName = (user?.displayName ?? user?.email ?? 'there').split(/[ @]/)[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">
            Welcome back, {firstName} 👋
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            Track and improve how your brand shows up in AI search.
          </p>
        </div>
        <Link to="/app/new-analysis">
          <Button>
            <PlusCircle size={18} /> New analysis
          </Button>
        </Link>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <QuickAction
          to="/app/new-analysis"
          icon={PlusCircle}
          title="Run an analysis"
          body="Analyze a website and see its AI visibility."
        />
        <QuickAction
          to="/app/projects"
          icon={FolderKanban}
          title="Manage projects"
          body="Organize brands, competitors, and history."
        />
        <QuickAction
          to="/app/reports"
          icon={FileBarChart}
          title="View reports"
          body="Revisit saved analyses and recommendations."
        />
      </div>

      {/* Projects */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Your projects</h2>
          <Link to="/app/projects" className="text-sm text-accent-blue hover:text-accent-indigo">
            View all
          </Link>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner className="h-7 w-7" />
          </div>
        ) : projects.length === 0 ? (
          <EmptyState
            icon={<Sparkles size={28} />}
            title="No projects yet"
            description="Run your first analysis to create a project and start tracking your AI visibility."
            action={
              <Link to="/app/new-analysis">
                <Button>
                  Analyze your website <ArrowRight size={16} />
                </Button>
              </Link>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <Link key={p.id} to={`/app/projects/${p.id}`}>
                <Card className="h-full transition hover:border-accent-blue/40">
                  <CardContent className="pt-5">
                    <div className="flex items-start justify-between">
                      <h3 className="font-semibold text-text-primary">{p.name}</h3>
                      {p.industry && <Badge tone="blue">{p.industry}</Badge>}
                    </div>
                    <p className="mt-1 truncate text-sm text-text-secondary">{p.websiteUrl}</p>
                    <p className="mt-4 text-xs text-text-muted">
                      Updated {formatDate(p.updatedAt)}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QuickAction({
  to,
  icon: Icon,
  title,
  body,
}: {
  to: string;
  icon: typeof PlusCircle;
  title: string;
  body: string;
}) {
  return (
    <Link to={to}>
      <Card className="h-full transition hover:border-accent-blue/40">
        <CardContent className="pt-5">
          <span className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-accent-blue/10 text-accent-blue">
            <Icon size={18} />
          </span>
          <h3 className="font-semibold text-text-primary">{title}</h3>
          <p className="mt-1 text-sm text-text-secondary">{body}</p>
        </CardContent>
      </Card>
    </Link>
  );
}
